import { calculateSettlement } from "@/lib/poker-calculator";
import type { CalculationResult, PlayerStackEntry } from "@/lib/poker-types";
import { parseAmount } from "@/lib/formatting";
import type { PlayerDraft, UsePlayersStateReturn } from "@/hooks/usePlayersState";
import type { UseCurrencyReturn } from "@/hooks/useCurrency";
import { useState } from "react";

export interface UseSettlementReturn {
  inputErrors: string[];
  result: CalculationResult | null;
  copyState: "idle" | "done" | "error";
  handleCalculate: () => void;
  handleCopySummary: () => void;
}

export function useSettlement(
  playersState: UsePlayersStateReturn,
  currency: UseCurrencyReturn,
): UseSettlementReturn {
  const [inputErrors, setInputErrors] = useState<string[]>([]);
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "done" | "error">("idle");

  const { players, hiddenFinalIds } = playersState;

  const buildEntries = (): { entries: PlayerStackEntry[]; errors: string[] } => {
    const errors: string[] = [];
    const entries: PlayerStackEntry[] = [];

    if (players.length < 2) {
      errors.push("Add at least 2 active players to calculate a settlement.");
    }

    players.forEach((player, index) => {
      const row = index + 1;
      const name = player.name.trim();
      const cashIn = parseAmount(player.cashIn);
      const chipsBought = parseAmount(player.chipsBought);
      const isHiddenFromFinal = hiddenFinalIds.has(player.id);
      const finalChips = isHiddenFromFinal ? 0 : parseAmount(player.finalChips);

      if (!name) {
        errors.push(`Row ${row}: player name is required.`);
      }
      if (cashIn === null) {
        errors.push(`Row ${row}: cash-in must be a valid number.`);
      } else if (cashIn < 0) {
        errors.push(`Row ${row}: cash-in cannot be negative.`);
      }
      if (chipsBought === null) {
        errors.push(`Row ${row}: chips bought must be a valid number.`);
      } else if (chipsBought < 0) {
        errors.push(`Row ${row}: chips bought cannot be negative.`);
      }
      if (!isHiddenFromFinal && finalChips === null) {
        errors.push(`Row ${row}: final chips must be a valid number.`);
      } else if (finalChips !== null && finalChips < 0) {
        errors.push(`Row ${row}: final chips cannot be negative.`);
      }

      if (
        name &&
        cashIn !== null && cashIn >= 0 &&
        chipsBought !== null && chipsBought >= 0 &&
        finalChips !== null && finalChips >= 0
      ) {
        entries.push({ id: player.id, name, cashIn, chipsBought, finalChips });
      }
    });

    return { entries, errors };
  };

  const handleCalculate = () => {
    const { entries, errors } = buildEntries();

    if (errors.length > 0) {
      setInputErrors(errors);
      setResult(null);
      return;
    }

    const calculation = calculateSettlement(entries);
    const calculationErrors = calculation.errors.map((e) => e.message);
    const mergedErrors = [...errors, ...calculationErrors];

    setInputErrors(mergedErrors);
    setResult(mergedErrors.length === 0 ? calculation : null);
    setCopyState("idle");
  };

  const handleCopySummary = async () => {
    if (!result || result.transfers.length === 0) return;

    const lines = result.transfers.map(
      (t) => `${t.fromName} pays ${t.toName} ${currency.formatCurrency(t.amount)}`,
    );

    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopyState("done");
    } catch {
      setCopyState("error");
    }
  };

  return {
    inputErrors,
    result,
    copyState,
    handleCalculate,
    handleCopySummary,
  };
}
